import { API } from '../api.js';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useBackButton from '../hooks/useBackButton';

// Mock billing history (normally from /api/subscriptions/history)
const MOCK_HISTORY = [
  { id:1, date:'2026-05-01', plan:'Pro',   amount:'฿1,490', cycle:'Monthly', status:'paid',   invoice:'INV-2026-005' },
  { id:2, date:'2026-04-01', plan:'Pro',   amount:'฿1,490', cycle:'Monthly', status:'paid',   invoice:'INV-2026-004' },
  { id:3, date:'2026-03-01', plan:'Basic', amount:'฿490',   cycle:'Monthly', status:'paid',   invoice:'INV-2026-003' },
  { id:4, date:'2026-02-01', plan:'Basic', amount:'฿490',   cycle:'Monthly', status:'paid',   invoice:'INV-2026-002' },
  { id:5, date:'2026-01-01', plan:'Free',  amount:'฿0',     cycle:'—',       status:'free',   invoice:'—' },
];

function formatCard(val) {
  return val.replace(/\D/g, '').slice(0,16).replace(/(.{4})/g, '$1 ').trim();
}
function formatExpiry(val) {
  return val.replace(/\D/g,'').slice(0,4).replace(/^(.{2})(.+)/, '$1/$2');
}

const STATUS_STYLE = {
  paid: { bg:'rgba(16,185,129,0.1)', color:'var(--accent3)', label:'Paid' },
  free: { bg:'rgba(148,163,184,0.1)', color:'var(--text3)', label:'Free' },
  failed:{ bg:'rgba(239,68,68,0.1)', color:'var(--accent2)', label:'Failed' },
};

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();

  const [plans, setPlans]       = useState([]);
  const [billing, setBilling]   = useState('monthly');
  const [loading, setLoading]   = useState(true);
  const [changing, setChanging] = useState(null);

  // Payment modal
  const [showPayModal,  setShowPayModal]  = useState(false);
  const [selectedPlan,  setSelectedPlan]  = useState(null);
  const [cardNum,  setCardNum]  = useState('');
  const [expiry,   setExpiry]   = useState('');
  const [cvc,      setCvc]      = useState('');
  const [cardName, setCardName] = useState('');
  const [payStep,  setPayStep]  = useState(1); // 1=card entry, 2=confirm, 3=success
  const [payLoading, setPayLoading] = useState(false);

  const [activeSection, setActiveSection] = useState('plans'); // plans | history | payment-methods

  useEffect(() => {
    fetch(API + '/api/plans')
      .then(r => r.json())
      .then(data => { setPlans(data); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!showPayModal) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closePayModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPayModal]);

  function openUpgradeModal(plan) {
    if (!plan || user?.plan_id === plan.id) return;
    if (plan.price_monthly === 0) {
      handleChangePlan(plan.id); // Free plan — no payment needed
      return;
    }
    setSelectedPlan(plan);
    setPayStep(1);
    setCardNum(''); setExpiry(''); setCvc(''); setCardName('');
    setShowPayModal(true);
  }

  async function handleChangePlan(planId) {
    setChanging(planId);
    try {
      const res = await fetch(API + '/api/subscriptions/current', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ plan_id: planId, billing_cycle: billing })
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await refreshUser();
    } catch (err) {
      console.error(err);
    }
    setChanging(null);
  }

  async function handlePaySubmit() {
    if (payStep === 1) {
      // Basic validation
      if (cardNum.replace(/\s/g,'').length < 16) return;
      if (expiry.length < 5) return;
      if (cvc.length < 3) return;
      setPayStep(2); return;
    }
    if (payStep === 2) {
      setPayLoading(true);
      await new Promise(r => setTimeout(r, 1400)); // Simulate payment
      await handleChangePlan(selectedPlan.id);
      setPayLoading(false);
      setPayStep(3);
    }
  }

  function closePayModal() { setShowPayModal(false); setSelectedPlan(null); setPayStep(1); }

  useBackButton(closePayModal, showPayModal);

  const currentPlan = plans.find(p => p.id === user?.plan_id);
  const yearly_save = (p) => p.price_monthly > 0 ? Math.round((1 - p.price_yearly / (p.price_monthly * 12)) * 100) : 0;

  const SECTIONS = [
    { key:'plans',           label:'Plans & Pricing', icon:'💎' },
    { key:'history',         label:'Billing History', icon:'📋' },
    { key:'payment-methods', label:'Payment Methods', icon:'💳' },
  ];

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', fontFamily:'inherit' }}>

      {/* Header */}
      <div style={{ background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'0 24px', height:60, display:'flex', alignItems:'center', gap:16, position:'sticky', top:0, zIndex:50 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontWeight:800, fontSize:18 }} onClick={() => navigate('/app')}>
          <div style={{ width:40, height:40, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
            <img src="/logo2.png" alt="Qeduvo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          Qeduvo
        </div>
        <div style={{ flex:1 }} />
        <button onClick={() => navigate('/settings')}
          style={{ background:'none', border:'1px solid var(--border)', borderRadius:8, padding:'7px 14px', cursor:'pointer', fontSize:13, fontWeight:600, color:'var(--text)' }}>
          ← Back to Settings
        </button>
      </div>

      <div className="sub-layout">

        {/* Sidebar */}
        <div className="sub-sidebar">
          {/* Current plan badge */}
          {currentPlan && (
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', background:'rgba(99,91,255,0.05)' }}>
              <div style={{ fontSize:11, color:'var(--text3)', fontWeight:700, textTransform:'uppercase', letterSpacing:0.5, marginBottom:6 }}>Current plan</div>
              <div style={{ fontWeight:800, fontSize:17, color:'var(--text)', display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:10, height:10, borderRadius:'50%', background:currentPlan.badge_color }} />
                {currentPlan.name}
              </div>
              <div style={{ fontSize:12, color:'var(--text3)', marginTop:3 }}>
                {currentPlan.price_monthly === 0 ? 'Free forever' : '฿' + currentPlan.price_monthly.toLocaleString() + '/month'}
              </div>
            </div>
          )}
          <div className="sub-sidebar-nav">
            {SECTIONS.map(s => (
              <div key={s.key} onClick={() => setActiveSection(s.key)}
                className={`sub-sidebar-item ${activeSection === s.key ? 'active' : ''}`}>
                <span>{s.icon}</span> {s.label}
              </div>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div>

          {/* ── Plans section ── */}
          {activeSection === 'plans' && (
            <div>
              <div style={{ marginBottom:32 }}>
                <h1 style={{ fontSize:26, fontWeight:900, marginBottom:6 }}>Plans & Pricing</h1>
                <p style={{ fontSize:14, color:'var(--text3)' }}>Upgrade or downgrade at any time. Changes take effect immediately.</p>
              </div>

              {/* Billing toggle */}
              <div style={{ display:'flex', background:'var(--surface2)', borderRadius:10, padding:4, gap:2, marginBottom:32, width:'fit-content' }}>
                {['monthly','yearly'].map(b => (
                  <button key={b} onClick={() => setBilling(b)}
                    style={{ padding:'8px 20px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:700, transition:'all 0.2s',
                      background: billing === b ? 'var(--surface)' : 'transparent',
                      color: billing === b ? 'var(--text)' : 'var(--text3)',
                      boxShadow: billing === b ? 'var(--shadow-sm)' : 'none' }}>
                    {b === 'monthly' ? 'Monthly' : 'Yearly'}
                    {b === 'yearly' && <span style={{ marginLeft:6, fontSize:10, background:'var(--accent3)', color:'#fff', borderRadius:8, padding:'2px 6px', fontWeight:800 }}>SAVE 17%</span>}
                  </button>
                ))}
              </div>

              {loading ? (
                <div style={{ color:'var(--text3)', textAlign:'center', padding:40 }}>Loading plans…</div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:20 }}>
                  {plans.map(plan => {
                    const isCurrent = user?.plan_id === plan.id;
                    const price     = billing === 'yearly' ? plan.price_yearly : plan.price_monthly;
                    const isPaid    = plan.price_monthly > 0;
                    const isEnterprise = plan.price_monthly === 0 && plan.id > 1;
                    const isHighlight  = plan.name === 'Pro';

                    return (
                      <div key={plan.id} style={{ background:'var(--surface)', border:`2px solid ${isCurrent ? plan.badge_color : 'var(--border)'}`, borderRadius:16, overflow:'hidden', position:'relative', display:'flex', flexDirection:'column' }}>
                        {isHighlight && !isCurrent && (
                          <div style={{ background:'var(--accent)', color:'#fff', textAlign:'center', fontSize:11, fontWeight:800, padding:'5px', letterSpacing:0.5 }}>⭐ MOST POPULAR</div>
                        )}
                        {isCurrent && (
                          <div style={{ background:plan.badge_color, color:'#fff', textAlign:'center', fontSize:11, fontWeight:800, padding:'5px' }}>✓ CURRENT PLAN</div>
                        )}
                        <div style={{ padding:'20px 20px 0' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                            <div style={{ width:8, height:8, borderRadius:'50%', background:plan.badge_color }} />
                            <span style={{ fontWeight:800, fontSize:16 }}>{plan.name}</span>
                          </div>
                          <div style={{ marginBottom:16 }}>
                            {isEnterprise ? (
                              <div style={{ fontSize:22, fontWeight:900 }}>Custom</div>
                            ) : (
                              <>
                                <span style={{ fontSize:26, fontWeight:900 }}>{plan.price_monthly === 0 ? 'Free' : '฿' + price.toLocaleString()}</span>
                                {isPaid && <span style={{ fontSize:12, color:'var(--text3)', marginLeft:4 }}>/{billing === 'yearly' ? 'yr' : 'mo'}</span>}
                              </>
                            )}
                            {billing === 'yearly' && yearly_save(plan) > 0 && (
                              <div style={{ fontSize:11, color:'var(--accent3)', fontWeight:600, marginTop:3 }}>Save ฿{(plan.price_monthly*12-plan.price_yearly).toLocaleString()}/year</div>
                            )}
                          </div>
                          <ul style={{ listStyle:'none', padding:0, margin:'0 0 20px', display:'flex', flexDirection:'column', gap:7 }}>
                            {(plan.features || []).map((f,i) => (
                              <li key={i} style={{ fontSize:12, color:'var(--text2)', display:'flex', gap:6 }}>
                                <span style={{ color:plan.badge_color, flexShrink:0 }}>✓</span> {f}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div style={{ padding:'0 20px 20px', marginTop:'auto' }}>
                          {isEnterprise ? (
                            <button style={{ width:'100%', padding:'11px', borderRadius:8, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', cursor:'pointer', fontWeight:700, fontSize:13 }}>
                              Contact sales
                            </button>
                          ) : isCurrent ? (
                            <button disabled style={{ width:'100%', padding:'11px', borderRadius:8, border:`1px solid ${plan.badge_color}44`, background:`${plan.badge_color}22`, color:plan.badge_color, cursor:'default', fontWeight:700, fontSize:13 }}>
                              ✓ Current plan
                            </button>
                          ) : (
                            <button onClick={() => openUpgradeModal(plan)} disabled={changing === plan.id}
                              style={{ width:'100%', padding:'11px', borderRadius:8, border:'none', background: changing === plan.id ? 'var(--surface3)' : isHighlight ? plan.badge_color : 'var(--accent)', color:'#fff', cursor: changing === plan.id ? 'not-allowed' : 'pointer', fontWeight:700, fontSize:13 }}>
                              {changing === plan.id ? '…' : (user?.plan_id > plan.id ? '↓ Downgrade' : '↑ Upgrade')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Billing History ── */}
          {activeSection === 'history' && (
            <div>
              <div style={{ marginBottom:28 }}>
                <h1 style={{ fontSize:26, fontWeight:900, marginBottom:6 }}>Billing History</h1>
                <p style={{ fontSize:14, color:'var(--text3)' }}>Your past payments and invoices.</p>
              </div>
              <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', overflowX:'auto' }}>
                {/* Table header */}
                <div style={{ display:'grid', gridTemplateColumns:'120px 1fr 1fr 100px 100px', minWidth:560, padding:'12px 20px', background:'var(--surface2)', borderBottom:'1px solid var(--border)', fontSize:11, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:0.5 }}>
                  <div>Date</div><div>Plan</div><div>Invoice</div><div>Amount</div><div>Status</div>
                </div>
                {MOCK_HISTORY.map((h,i) => {
                  const s = STATUS_STYLE[h.status] || STATUS_STYLE.paid;
                  return (
                    <div key={h.id} style={{ display:'grid', gridTemplateColumns:'120px 1fr 1fr 100px 100px', minWidth:560, padding:'14px 20px', borderBottom: i < MOCK_HISTORY.length-1 ? '1px solid var(--border)' : 'none', alignItems:'center' }}>
                      <div style={{ fontSize:13, color:'var(--text2)' }}>{h.date}</div>
                      <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{h.plan}</div>
                      <div style={{ fontSize:12, color:'var(--accent)', cursor:'pointer', textDecoration:'underline' }}>{h.invoice}</div>
                      <div style={{ fontSize:13, fontWeight:700 }}>{h.amount}</div>
                      <div>
                        <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:10, background:s.bg, color:s.color }}>{s.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Payment Methods ── */}
          {activeSection === 'payment-methods' && (
            <div>
              <div style={{ marginBottom:28 }}>
                <h1 style={{ fontSize:26, fontWeight:900, marginBottom:6 }}>Payment Methods</h1>
                <p style={{ fontSize:14, color:'var(--text3)' }}>Manage your saved payment methods.</p>
              </div>
              <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, padding:24 }}>
                <div style={{ textAlign:'center', padding:'32px 0', color:'var(--text3)' }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>💳</div>
                  <div style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>No cards on file</div>
                  <div style={{ fontSize:13 }}>Add a payment method to upgrade your plan.</div>
                </div>
                <button onClick={() => setActiveSection('plans')}
                  style={{ display:'block', width:'100%', padding:'12px', borderRadius:10, border:'none', background:'var(--accent)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:14 }}>
                  + Add Payment Method
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Payment Modal ── */}
      {showPayModal && selectedPlan && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}
          onClick={e => e.target === e.currentTarget && closePayModal()}>
          <div style={{ background:'var(--surface)', borderRadius:20, width:'100%', maxWidth:440, overflow:'hidden', boxShadow:'var(--shadow-lg)' }}>

            {/* Modal header */}
            <div style={{ padding:'20px 24px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontWeight:800, fontSize:18 }}>
                  {payStep === 3 ? '🎉 Payment successful!' : payStep === 2 ? 'Confirm payment' : 'Add payment details'}
                </div>
                {payStep < 3 && <div style={{ fontSize:13, color:'var(--text3)', marginTop:3 }}>{selectedPlan.name} · ฿{(billing==='yearly'?selectedPlan.price_yearly:selectedPlan.price_monthly).toLocaleString()}/{billing==='yearly'?'yr':'mo'}</div>}
              </div>
              <button onClick={closePayModal} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:22, lineHeight:1 }}>×</button>
            </div>

            <div style={{ padding:'20px 24px 24px' }}>
              {/* Step dots */}
              {payStep < 3 && (
                <div style={{ display:'flex', gap:6, marginBottom:24 }}>
                  {[1,2].map(n => (
                    <div key={n} style={{ height:3, flex:1, borderRadius:3, background: payStep >= n ? 'var(--accent)' : 'var(--border)', transition:'background 0.3s' }} />
                  ))}
                </div>
              )}

              {/* Step 1 — Card entry */}
              {payStep === 1 && (
                <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  <div>
                    <label style={{ fontSize:12, fontWeight:700, color:'var(--text3)', display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>Card number</label>
                    <div style={{ display:'flex', alignItems:'center', border:'1px solid var(--border)', borderRadius:10, background:'var(--surface2)', padding:'12px 14px', gap:10 }}>
                      <span style={{ fontSize:20 }}>💳</span>
                      <input value={cardNum} onChange={e => setCardNum(formatCard(e.target.value))}
                        placeholder="1234 5678 9012 3456" maxLength={19}
                        style={{ flex:1, border:'none', background:'transparent', color:'var(--text)', fontSize:15, outline:'none', fontFamily:'monospace', letterSpacing:1 }} />
                    </div>
                  </div>
                  <div className="form-row-2">
                    <div>
                      <label style={{ fontSize:12, fontWeight:700, color:'var(--text3)', display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>Expiry</label>
                      <input value={expiry} onChange={e => setExpiry(formatExpiry(e.target.value))}
                        placeholder="MM/YY" maxLength={5}
                        style={{ width:'100%', padding:'12px 14px', borderRadius:10, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', fontSize:15, outline:'none', fontFamily:'monospace' }} />
                    </div>
                    <div>
                      <label style={{ fontSize:12, fontWeight:700, color:'var(--text3)', display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>CVC</label>
                      <input value={cvc} onChange={e => setCvc(e.target.value.replace(/\D/,'').slice(0,4))}
                        placeholder="123" maxLength={4} type="password"
                        style={{ width:'100%', padding:'12px 14px', borderRadius:10, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', fontSize:15, outline:'none', fontFamily:'monospace' }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize:12, fontWeight:700, color:'var(--text3)', display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>Name on card</label>
                    <input value={cardName} onChange={e => setCardName(e.target.value)}
                      placeholder="Full name"
                      style={{ width:'100%', padding:'12px 14px', borderRadius:10, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', fontSize:14, outline:'none', fontFamily:'inherit' }} />
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:11, color:'var(--text3)' }}>
                    <span>🔒</span> Secured by 256-bit SSL encryption. We never store your card details.
                  </div>
                </div>
              )}

              {/* Step 2 — Confirm */}
              {payStep === 2 && (
                <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  <div style={{ background:'var(--surface2)', borderRadius:12, padding:'16px 20px' }}>
                    <div style={{ fontSize:12, color:'var(--text3)', marginBottom:12, fontWeight:700, textTransform:'uppercase', letterSpacing:0.5 }}>Order summary</div>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                      <span style={{ fontSize:14 }}>{selectedPlan.name} plan ({billing})</span>
                      <span style={{ fontWeight:700 }}>฿{(billing==='yearly'?selectedPlan.price_yearly:selectedPlan.price_monthly).toLocaleString()}</span>
                    </div>
                    {billing === 'yearly' && yearly_save(selectedPlan) > 0 && (
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, color:'var(--accent3)' }}>
                        <span style={{ fontSize:13 }}>Annual discount</span>
                        <span style={{ fontWeight:700 }}>-฿{(selectedPlan.price_monthly*12-selectedPlan.price_yearly).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="divider" style={{ margin:'12px 0' }} />
                    <div style={{ display:'flex', justifyContent:'space-between', fontWeight:800 }}>
                      <span>Total today</span>
                      <span style={{ color:'var(--accent)' }}>฿{(billing==='yearly'?selectedPlan.price_yearly:selectedPlan.price_monthly).toLocaleString()}</span>
                    </div>
                  </div>
                  <div style={{ background:'var(--surface2)', borderRadius:12, padding:'12px 20px', display:'flex', alignItems:'center', gap:12 }}>
                    <span style={{ fontSize:24 }}>💳</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600 }}>Card ending ···· {cardNum.replace(/\s/g,'').slice(-4)}</div>
                      <div style={{ fontSize:11, color:'var(--text3)' }}>Expires {expiry}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3 — Success */}
              {payStep === 3 && (
                <div style={{ textAlign:'center', padding:'20px 0 8px' }}>
                  <div style={{ fontSize:64, marginBottom:16 }}>🎉</div>
                  <div style={{ fontSize:18, fontWeight:800, marginBottom:8 }}>You're now on {selectedPlan.name}!</div>
                  <div style={{ fontSize:14, color:'var(--text2)', lineHeight:1.6, marginBottom:24 }}>
                    Your subscription is active. Enjoy all the benefits of {selectedPlan.name}.
                  </div>
                  <div style={{ display:'flex', gap:10 }}>
                    <button onClick={closePayModal}
                      style={{ flex:1, padding:'12px', borderRadius:10, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', cursor:'pointer', fontWeight:700 }}>
                      Close
                    </button>
                    <button onClick={() => { closePayModal(); navigate('/app'); }}
                      style={{ flex:2, padding:'12px', borderRadius:10, border:'none', background:'var(--accent)', color:'#fff', cursor:'pointer', fontWeight:700 }}>
                      Go to community →
                    </button>
                  </div>
                </div>
              )}

              {/* Action button (steps 1+2) */}
              {payStep < 3 && (
                <button onClick={handlePaySubmit} disabled={payLoading}
                  style={{ width:'100%', marginTop:20, padding:'14px', borderRadius:10, border:'none', background: payLoading ? 'var(--surface3)' : 'var(--accent)', color: payLoading ? 'var(--text3)' : '#fff', cursor: payLoading ? 'not-allowed' : 'pointer', fontWeight:800, fontSize:15 }}>
                  {payLoading ? 'Processing…' : payStep === 1 ? 'Review payment →' : `Pay ฿${(billing==='yearly'?selectedPlan.price_yearly:selectedPlan.price_monthly).toLocaleString()}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
